import React, { useMemo } from 'react';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '@/lib/utils';

// Constants - Hebrew translations
const PASSWORD_REQUIREMENTS = [
  { regex: /.{8,}/, text: 'לפחות 8 תווים' },
  { regex: /[0-9]/, text: 'לפחות ספרה אחת' },
  { regex: /[a-z]/, text: 'לפחות אות קטנה אחת' },
  { regex: /[A-Z]/, text: 'לפחות אות גדולה אחת' },
  { regex: /[!-\/:-@[-`{-~]/, text: 'לפחות תו מיוחד אחד' },
] as const;

type StrengthScore = 0 | 1 | 2 | 3 | 4 | 5;

const STRENGTH_CONFIG = {
  colors: {
    0: 'bg-border',
    1: 'bg-red-500',
    2: 'bg-orange-500',
    3: 'bg-amber-500',
    4: 'bg-amber-700',
    5: 'bg-emerald-500',
  } satisfies Record<StrengthScore, string>,
  texts: {
    0: 'הזן סיסמה',
    1: 'סיסמה חלשה',
    2: 'סיסמה בינונית',
    3: 'סיסמה חזקה!',
    4: 'סיסמה חזקה מאוד!',
  } satisfies Record<Exclude<StrengthScore, 5>, string>,
} as const;

// Types
type Requirement = {
  met: boolean;
  text: string;
};

type PasswordStrength = {
  score: StrengthScore;
  requirements: Requirement[];
};

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  showStrength?: boolean;
  className?: string;
}

const PasswordInput = ({
  id,
  value,
  onChange,
  label = 'סיסמה',
  placeholder = '••••••',
  required = false,
  showStrength = true,
  className,
}: PasswordInputProps) => {
  const [isVisible, setIsVisible] = React.useState(false);

  const calculateStrength = useMemo((): PasswordStrength => {
    const requirements = PASSWORD_REQUIREMENTS.map((req) => ({
      met: req.regex.test(value),
      text: req.text,
    }));

    return {
      score: requirements.filter((req) => req.met).length as StrengthScore,
      requirements,
    };
  }, [value]);

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label htmlFor={id}>{label}</Label>
      )}
      
      <div className="relative">
        <Input
          id={id}
          type={isVisible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          aria-invalid={showStrength && value.length > 0 && calculateStrength.score < 4}
          aria-describedby={showStrength ? `${id}-strength` : undefined}
          className="text-right pl-10"
        />
        <button
          type="button"
          onClick={() => setIsVisible((prev) => !prev)}
          aria-label={isVisible ? 'הסתר סיסמה' : 'הצג סיסמה'}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {showStrength && value.length > 0 && (
        <>
          {/* Strength Meter Bars */}
          <div className="flex gap-1" id={`${id}-strength`}>
            <div
              className={`h-1 rounded-full w-full transition-colors ${
                calculateStrength.score >= 1 ? 'bg-red-400' : 'bg-border'
              }`}
            />
            <div
              className={`h-1 rounded-full w-full transition-colors ${
                calculateStrength.score >= 2 ? 'bg-orange-400' : 'bg-border'
              }`}
            />
            <div
              className={`h-1 rounded-full w-full transition-colors ${
                calculateStrength.score >= 3 ? 'bg-amber-400' : 'bg-border'
              }`}
            />
            <div
              className={`h-1 rounded-full w-full transition-colors ${
                calculateStrength.score >= 4 ? 'bg-emerald-400' : 'bg-border'
              }`}
            />
            <div
              className={`h-1 rounded-full w-full transition-colors ${
                calculateStrength.score >= 5 ? 'bg-emerald-500' : 'bg-border'
              }`}
            />
          </div>

          {/* Strength Text */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">דרישות:</span>
            <span
              className={cn(
                'font-medium',
                calculateStrength.score <= 1 && 'text-red-500',
                calculateStrength.score === 2 && 'text-orange-500',
                calculateStrength.score === 3 && 'text-amber-500',
                calculateStrength.score >= 4 && 'text-emerald-500'
              )}
            >
              {
                STRENGTH_CONFIG.texts[
                  Math.min(calculateStrength.score, 4) as keyof typeof STRENGTH_CONFIG.texts
                ]
              }
            </span>
          </div>

          {/* Requirements Checklist */}
          <div className="space-y-1.5">
            {calculateStrength.requirements.map((req, index) => (
              <div
                key={index}
                className={cn(
                  'flex items-center gap-2 text-xs transition-colors',
                  req.met ? 'text-emerald-600' : 'text-muted-foreground'
                )}
              >
                {req.met ? (
                  <Check className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <X className="h-3 w-3" aria-hidden="true" />
                )}
                <span>
                  {req.text}
                  <span className="sr-only">
                    {req.met ? ' - הדרישה מתקיימת' : ' - הדרישה לא מתקיימת'}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export { PasswordInput };
export type { PasswordInputProps };
